import React, { Component, createRef } from 'react';
import axios from 'axios';
import { NavLink } from 'react-router-dom';
import { hostname } from '../host';
import { getAccessToken } from '../auth/AuthService';
import '../theme/misanthropy.css';

import {
  ArrowBackIcon, GroupIcon, RemoveIcon, PersonAddIcon,
  RestoreIcon, SaveIcon, SendIcon, RobotIcon
} from './MisIcons';

class MisTableView extends Component {
  constructor(props) {
    super(props);
    this.state = {
      session: null,
      messages: [],
      slots: [],
      personas: [],
      pcs: [],
      loading: true,
      // Party panel starts open on desktop, closed on a narrow screen (where it's
      // an overlay drawer) so first paint shows the conversation, not the panel.
      sidebarOpen: typeof window === 'undefined' || window.innerWidth > 720,
      gmInput: '',
      sending: false,
      generating: false,
      respondHint: false, // pulse the Respond button after a GM send, until it's clicked
      savePoints: [],
      savepointDialogOpen: false,
      savepointLabel: '',
      addSlotPcId: '',
      addSlotPersonaId: '',
      addingSlot: false,
      kbInset: 0 // px the on-screen keyboard covers; lifts the surface above it (mobile)
    };
    this.messagesEndRef = createRef();
    this.messagesContainerRef = createRef();
    this.onKey = this.onKey.bind(this);
    this.onViewport = this.onViewport.bind(this);
  }

  componentDidMount() {
    this.load();
    document.addEventListener('keydown', this.onKey);
    const vv = window.visualViewport;
    if (vv) {
      vv.addEventListener('resize', this.onViewport);
      vv.addEventListener('scroll', this.onViewport);
    }
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.onKey);
    const vv = window.visualViewport;
    if (vv) {
      vv.removeEventListener('resize', this.onViewport);
      vv.removeEventListener('scroll', this.onViewport);
    }
  }

  // The keyboard shrinks the visual viewport but not the layout viewport, so the
  // bottom input bar hides behind it. Measure the covered strip and lift the whole
  // surface by that much. Threshold guards against address-bar show/hide (~60px).
  onViewport() {
    const vv = window.visualViewport;
    if (!vv) return;
    const overlap = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
    const kbInset = overlap > 120 ? Math.round(overlap) : 0;
    if (kbInset !== this.state.kbInset) {
      this.setState({ kbInset }, this.scrollToBottom);
    }
  }

  componentDidUpdate(_, prevState) {
    if (prevState.messages.length !== this.state.messages.length) {
      this.scrollToBottom();
    }
  }

  onKey(e) {
    if (e.key === 'Escape' && this.state.savepointDialogOpen) {
      this.setState({ savepointDialogOpen: false });
    }
  }

  get headers() { return { Authorization: 'Bearer ' + getAccessToken() }; }

  get sessionId() { return this.props.match.params.sessionId; }

  load() {
    const base = hostname() + 'rest/misanthropy/';
    Promise.all([
      axios.get(base + 'sessions/' + this.sessionId, { headers: this.headers }),
      axios.get(base + 'sessions/' + this.sessionId + '/messages', { headers: this.headers }),
      axios.get(base + 'sessions/' + this.sessionId + '/savepoints', { headers: this.headers }),
      axios.get(base + 'personas', { headers: this.headers }),
      axios.get(base + 'pcs', { headers: this.headers })
    ]).then(([sessionRes, msgRes, spRes, personaRes, pcRes]) => {
      this.setState({
        session: sessionRes.data,
        slots: sessionRes.data.slots || [],
        messages: msgRes.data || [],
        savePoints: spRes.data || [],
        personas: personaRes.data || [],
        pcs: pcRes.data || [],
        loading: false
      }, this.scrollToBottom);
    }).catch(() => this.setState({ loading: false }));
  }

  scrollToBottom() {
    const el = this.messagesContainerRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }

  send() {
    const { gmInput, sending } = this.state;
    if (!gmInput.trim() || sending) return;
    this.setState({ sending: true });
    axios.post(
      hostname() + 'rest/misanthropy/sessions/' + this.sessionId + '/send',
      { text: gmInput.trim() },
      { headers: this.headers }
    ).then(res => {
      this.setState(prev => ({
        messages: [...prev.messages, res.data],
        gmInput: '',
        sending: false,
        respondHint: true
      }));
    }).catch(err => {
      console.error(err);
      this.setState({ sending: false });
    });
  }

  generateResponses() {
    this.setState({ generating: true, respondHint: false });
    axios.post(
      hostname() + 'rest/misanthropy/sessions/' + this.sessionId + '/respond',
      {},
      { headers: this.headers }
    ).then(res => {
      this.setState(prev => ({
        messages: [...prev.messages, ...(res.data || [])],
        generating: false
      }));
    }).catch(err => {
      console.error(err);
      this.setState({ generating: false });
    });
  }

  addSlot() {
    const { addSlotPcId, addSlotPersonaId, addingSlot } = this.state;
    if (!addSlotPcId || !addSlotPersonaId || addingSlot) return;
    this.setState({ addingSlot: true });
    axios.post(
      hostname() + 'rest/misanthropy/sessions/' + this.sessionId + '/slots',
      { pcId: addSlotPcId, personaId: addSlotPersonaId },
      { headers: this.headers }
    ).then(res => {
      this.setState(prev => ({
        slots: [...prev.slots, res.data],
        addSlotPcId: '',
        addSlotPersonaId: '',
        addingSlot: false
      }));
    }).catch(err => {
      console.error(err);
      this.setState({ addingSlot: false });
    });
  }

  removeSlot(slotId) {
    axios.delete(
      hostname() + 'rest/misanthropy/sessions/' + this.sessionId + '/slots/' + slotId,
      { headers: this.headers }
    ).then(() => {
      this.setState(prev => ({ slots: prev.slots.filter(s => s.id !== slotId) }));
    }).catch(err => console.error(err));
  }

  savePoint() {
    const { savepointLabel } = this.state;
    axios.post(
      hostname() + 'rest/misanthropy/sessions/' + this.sessionId + '/savepoints',
      { label: savepointLabel.trim() || 'Save Point' },
      { headers: this.headers }
    ).then(res => {
      this.setState(prev => ({
        savePoints: [...prev.savePoints, res.data],
        savepointLabel: ''
      }));
    }).catch(err => console.error(err));
  }

  rollback(spId) {
    if (!window.confirm('Roll back to this save point? Messages after it will be deleted.')) return;
    const base = hostname() + 'rest/misanthropy/sessions/' + this.sessionId;
    axios.post(base + '/savepoints/' + spId + '/rollback', {}, { headers: this.headers })
      .then(() => Promise.all([
        axios.get(base + '/messages', { headers: this.headers }),
        axios.get(base + '/savepoints', { headers: this.headers })
      ]))
      .then(([msgRes, spRes]) => {
        this.setState({ messages: msgRes.data || [], savePoints: spRes.data || [] },
          this.scrollToBottom);
      })
      .catch(err => console.error(err));
  }

  getSlotInfo(slotId) {
    const { slots, pcs, personas } = this.state;
    const slot = slots.find(s => s.id === slotId);
    if (!slot) return { pcName: '?', personaName: '?', avatarData: null };
    const pc = pcs.find(p => p.id === slot.pcId);
    const persona = personas.find(p => p.id === slot.personaId);
    return {
      pcName: pc ? pc.name : '?',
      personaName: persona ? persona.name : '?',
      avatarData: persona ? persona.avatarData : null
    };
  }

  renderText(text) {
    if (!text) return null;
    const parts = text.split(/(\*[^*]+\*)/);
    return parts.map((part, i) =>
      part.startsWith('*') && part.endsWith('*') && part.length > 2
        ? <em key={i}>{part.slice(1, -1)}</em>
        : part
    );
  }

  renderAvatar(src, name, sizeClass) {
    const initial = name ? name.charAt(0) : '?';
    return src
      ? <img className={'mis-avatar ' + sizeClass} src={src} alt={name || 'avatar'} />
      : <span className={'mis-avatar ' + sizeClass}>{initial}</span>;
  }

  renderMessage(msg) {
    if (msg.senderType === 'GM') {
      return (
        <div className="mis-msg is-gm" key={msg.id}>
          <div className="mis-msg-col">
            <p className="mis-msg-name">GM</p>
            <div className="mis-bubble mis-bubble-gm">{this.renderText(msg.text)}</div>
          </div>
        </div>
      );
    }

    if (msg.senderType === 'SYSTEM') {
      return (
        <div className="mis-msg-sys" key={msg.id}>— {msg.text} —</div>
      );
    }

    const { pcName, personaName, avatarData } = this.getSlotInfo(msg.slotId);
    return (
      <div className="mis-msg" key={msg.id}>
        {this.renderAvatar(avatarData, pcName, 'mis-avatar-36')}
        <div className="mis-msg-col">
          <p className="mis-msg-name">
            {personaName} <span className="mis-msg-pc">({pcName})</span>
          </p>
          <div className="mis-bubble mis-bubble-npc">{this.renderText(msg.text)}</div>
        </div>
      </div>
    );
  }

  render() {
    const {
      session, messages, slots, personas, pcs, loading, sidebarOpen,
      gmInput, sending, generating, respondHint, savePoints, savepointDialogOpen, savepointLabel,
      addSlotPcId, addSlotPersonaId, addingSlot, kbInset
    } = this.state;

    if (loading) {
      return (
        <div className="mis-tv" style={{ alignItems: 'center', justifyContent: 'center' }}>
          <div className="mis-spinner" />
        </div>
      );
    }

    return (
      <div className="mis-tv" style={{ bottom: kbInset }}>

        {/* Header bar */}
        <div className="mis-tv-bar">
          <NavLink className="mis-icon-btn" to="/misanthropy" title="Back to sessions">
            <ArrowBackIcon />
          </NavLink>
          <h1 className="mis-tv-title">{session ? session.title : ''}</h1>
          <button className={'mis-btn mis-btn-quiet mis-btn-sm' + (sidebarOpen ? ' is-on' : '')}
            onClick={() => this.setState(p => ({ sidebarOpen: !p.sidebarOpen }))}>
            <GroupIcon /> Party ({slots.length})
          </button>
          <button className="mis-btn mis-btn-quiet mis-btn-sm" title="Save Point"
            onClick={() => this.setState({ savepointDialogOpen: true })}>
            <SaveIcon /> <span className="mis-tv-btn-label">Save Point</span>
          </button>
        </div>

        {/* Body */}
        <div className="mis-tv-body">

          {/* Party sidebar — inline column on desktop, overlay drawer on mobile */}
          {sidebarOpen &&
            <div className="mis-party-backdrop" onClick={() => this.setState({ sidebarOpen: false })} />}
          {sidebarOpen &&
            <div className="mis-party">
              <p className="mis-party-h">Party</p>

              {slots.map(slot => {
                const pc = pcs.find(p => p.id === slot.pcId);
                const persona = personas.find(p => p.id === slot.personaId);
                return (
                  <div className="mis-party-row" key={slot.id}>
                    {this.renderAvatar(persona && persona.avatarData ? persona.avatarData : null,
                      pc ? pc.name : '?', 'mis-avatar-32')}
                    <div className="mis-party-names">
                      <p className="mis-party-pc">{pc ? pc.name : '?'}</p>
                      <p className="mis-party-persona">{persona ? persona.name : '?'}</p>
                    </div>
                    <button className="mis-icon-btn mis-icon-btn-sm" title="Remove player"
                      onClick={() => this.removeSlot(slot.id)}><RemoveIcon /></button>
                  </div>
                );
              })}

              {slots.length === 0 &&
                <p className="mis-party-persona">No players seated.</p>}

              <hr className="mis-divider" style={{ margin: '8px 0' }} />

              <p className="mis-party-h">Add Player</p>

              <div className="mis-field" style={{ margin: 0 }}>
                <label className="mis-field-label">Character</label>
                <select className="mis-select" value={addSlotPcId}
                  onChange={e => this.setState({ addSlotPcId: e.target.value })}>
                  <option value="">Select…</option>
                  {pcs.map(pc => <option key={pc.id} value={pc.id}>{pc.name}</option>)}
                </select>
              </div>

              <div className="mis-field" style={{ margin: 0 }}>
                <label className="mis-field-label">Persona</label>
                <select className="mis-select" value={addSlotPersonaId}
                  onChange={e => this.setState({ addSlotPersonaId: e.target.value })}>
                  <option value="">Select…</option>
                  {personas.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>

              <button className="mis-btn mis-btn-primary mis-btn-sm"
                disabled={!addSlotPcId || !addSlotPersonaId || addingSlot}
                onClick={() => this.addSlot()}>
                <PersonAddIcon /> Add
              </button>
            </div>}

          {/* Message area + input */}
          <div className="mis-log">
            <div className="mis-log-scroll" ref={this.messagesContainerRef}>
              <div className="mis-log-inner">
                {messages.map(msg => this.renderMessage(msg))}
                {messages.length === 0 &&
                  <p className="mis-log-empty">No messages yet. Begin the session below.</p>}
              </div>
            </div>

            {/* GM input bar */}
            <div className="mis-inputbar">
              <div className="mis-inputbar-inner">
                <textarea className="mis-textarea" rows={1}
                  placeholder="Speak or act as GM…"
                  value={gmInput}
                  onChange={e => this.setState({ gmInput: e.target.value })}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); this.send(); } }}
                  disabled={sending || generating} />
                <button className="mis-icon-btn" title="Send" style={{ color: 'var(--nf-accent-hover)' }}
                  disabled={!gmInput.trim() || sending || generating}
                  onClick={() => this.send()}>
                  <SendIcon />
                </button>
                <button className={'mis-btn mis-btn-ghost mis-btn-sm'
                    + (respondHint && !sending && !generating && slots.length > 0 ? ' is-pulsing' : '')}
                  disabled={sending || generating || slots.length === 0}
                  onClick={() => this.generateResponses()}>
                  {generating ? <span className="mis-spinner mis-spinner-sm" /> : <RobotIcon />}
                  Respond
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Save point dialog */}
        {savepointDialogOpen &&
          <div className="mis-backdrop" role="dialog" aria-modal="true"
            onClick={e => { if (e.currentTarget === e.target) this.setState({ savepointDialogOpen: false }); }}>
            <div className="mis-modal mis-modal-sm">
              <div className="mis-modal-head">
                <h2 className="mis-modal-title">Save Points</h2>
              </div>
              <div className="mis-modal-body">
                {savePoints.length === 0 &&
                  <p className="mis-empty" style={{ marginBottom: 12 }}>No save points yet.</p>}
                {savePoints.map(sp => (
                  <div className="mis-party-row" key={sp.id} style={{ marginBottom: 8 }}>
                    <div className="mis-party-names">
                      <p className="mis-party-pc">{sp.label}</p>
                      <p className="mis-party-persona">{new Date(sp.createdAt).toLocaleString()}</p>
                    </div>
                    <button className="mis-icon-btn mis-icon-btn-sm" title="Roll back to here"
                      onClick={() => this.rollback(sp.id)}><RestoreIcon /></button>
                  </div>
                ))}
                <hr className="mis-divider" />
                <div className="mis-field" style={{ margin: 0 }}>
                  <label className="mis-field-label">Create new save point</label>
                  <input className="mis-input" value={savepointLabel}
                    placeholder="e.g. Before the dungeon entrance"
                    onChange={e => this.setState({ savepointLabel: e.target.value })}
                    onKeyDown={e => { if (e.key === 'Enter') this.savePoint(); }} />
                </div>
              </div>
              <div className="mis-modal-foot">
                <button className="mis-btn mis-btn-quiet"
                  onClick={() => this.setState({ savepointDialogOpen: false })}>Close</button>
                <button className="mis-btn mis-btn-primary" onClick={() => this.savePoint()}>
                  <SaveIcon /> Save
                </button>
              </div>
            </div>
          </div>}
      </div>
    );
  }
}

export default MisTableView;
